import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import UploadAdCreativeButton from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/UploadAdCreativeButton'
import SkeletonForm from '../../../components/Form/Skeleton'
import SuccessModal from '../../../features/components/Modals/SuccessModal'
import UploadGoogleAdCreativeForm from './UploadGoogleAdCreativeForm'
import FormDrawerWrapper from '../../../features/components/FormDrawerWrapper'
import SocialAuthChecker from '../../../features/components/SocialAuthChecker'
import RightWrongDatesMessage from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/RightWrongDatesMessage'

import { ReactComponent as GoogleIcon } from '../../../assets/logos/google-icon.svg'

import useIsInternalAdCreation from '../../../features/hooks/internalAdHooks/useIsInternalAdCreation'
import useClearAssetsGroupCreate from '../AdForms/GoogleAssetGroup/GoogleAssetGroupCreate/hook'
import useRedirectFormDrawer from '../../../features/components/FormDrawerWrapper/useRedirectFormDrawer'
import useUploadAdCreativeConstants from '../../Multiplatform/UploadAdCreative/useUploadAdCreativeConstants'

import { redirectTo } from '../../../helpers/url'
import { checkIfCampaignHasLineItem } from '../CampaignForms/helpers'

import { clearRightUploadAdData } from '../../../modules/actions/rights'
import { clearAssetGroups } from '../../../modules/actions/assets'
import { clearAds } from '../../../modules/actions/ads'

import { selectedGoogleAdAccountSelector } from '../../../modules/selectors/adAccounts'

import { UPLOAD_GOOGLE_AD_CREATIVE } from '../../../constants/forms'
import { GOOGLE_PLATFORM, PLATFORM_LABELS } from '../../../constants/selectLists/platformList'
import { ROUTE_PARAMS, ROUTES } from '../../../constants/routes'
import { MODAL_TYPE } from '../../../constants/other'

const initialRedirectionParams = {
  [ROUTE_PARAMS.campaignId]: ''
}

const initialCampaignType = ''

const UploadGoogleAdCreative = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  // modal related state data
  const [redirectionParams, setRedirectionParams] = useState(initialRedirectionParams)
  const [campaignType, setCampaignType] = useState(initialCampaignType)

  const { id: googleAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  const isInternalAdCreation = useIsInternalAdCreation()
  const { isFormLoading, isRightEndDateInThePast } = useUploadAdCreativeConstants()

  const isCampaignWithLineItem = checkIfCampaignHasLineItem(campaignType)

  const clearCreateAssetsGroupHandler = useClearAssetsGroupCreate()
  const handleSuccessRedirect = useRedirectFormDrawer(
    `${ROUTES.rightsSummary}?message=${MODAL_TYPE.rightGoogleAdUploaded}`
  )

  const modalRedirectionHandler = useCallback(() => {
    const campaignHasLineItem = !!redirectionParams[ROUTE_PARAMS.lineItemId]
    const URL = campaignHasLineItem ? ROUTES.lineItemsSummary : ROUTES.campaignsSummary

    redirectTo(URL, {
      ...redirectionParams,
      [ROUTE_PARAMS.platform]: GOOGLE_PLATFORM,
      [ROUTE_PARAMS.adAccount]: googleAdAccountId
    })
  }, [redirectionParams, googleAdAccountId])

  const onModalCloseHandler = useCallback(() => {
    setRedirectionParams(initialRedirectionParams)
    setCampaignType(initialCampaignType)
  }, [])

  const handleSuccessUploadCreative = useCallback(
    (redirectionParams, campaignType) => {
      setRedirectionParams(redirectionParams)
      setCampaignType(campaignType)

      dispatch(clearAds())
      handleSuccessRedirect()
    },
    [dispatch, handleSuccessRedirect]
  )

  const ButtonTrigger = useCallback(props => <UploadAdCreativeButton Icon={GoogleIcon} text="Display" {...props} />, [])

  const onAfterFormCloseHandler = useCallback(() => {
    dispatch(clearRightUploadAdData(GOOGLE_PLATFORM))

    clearCreateAssetsGroupHandler()
    dispatch(clearAssetGroups())
  }, [dispatch, clearCreateAssetsGroupHandler])

  const renderDrawerContent = useCallback(() => {
    if (isRightEndDateInThePast) {
      return <RightWrongDatesMessage />
    } else {
      return <UploadGoogleAdCreativeForm onSuccessSubmit={handleSuccessUploadCreative} />
    }
  }, [isRightEndDateInThePast, handleSuccessUploadCreative])

  return (
    <>
      <FormDrawerWrapper
        formName={UPLOAD_GOOGLE_AD_CREATIVE}
        closeOnSubmit={false}
        OpenDrawerTrigger={ButtonTrigger}
        onAfterFormClose={onAfterFormCloseHandler}
        isFormLoading={isFormLoading}
        SkeletonFormComponent={SkeletonForm}
      >
        <SocialAuthChecker
          platform={GOOGLE_PLATFORM}
          authFailureText={`Please authenticate with ${PLATFORM_LABELS[GOOGLE_PLATFORM]} to upload ad creative.`}
        >
          {renderDrawerContent()}
        </SocialAuthChecker>
      </FormDrawerWrapper>
      <SuccessModal
        successButtonText="View Ad"
        successButtonAction={modalRedirectionHandler}
        modalType={MODAL_TYPE.rightGoogleAdUploaded}
        onAfterClose={onModalCloseHandler}
        showCloseButton
      >
        <p>
          {t(
            isInternalAdCreation
              ? `Your ${
                  isCampaignWithLineItem ? 'ad' : 'asset group'
                } has been uploaded successfully and is pending review.`
              : `Your ${isCampaignWithLineItem ? 'ad' : 'asset group'} has been uploaded successfully.`
          )}
        </p>
      </SuccessModal>
    </>
  )
}

export default UploadGoogleAdCreative
